import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Tables } from '@/integrations/supabase/types';

type Vendor = Tables<'vendors'>;

export const useIsAdmin = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'admin' });
      
      if (error) {
        console.error('Error checking admin role:', error);
        return false;
      }
      
      return data === true;
    },
    enabled: !!user?.id,
  });
};

export const usePendingVendors = () => {
  return useQuery({
    queryKey: ['pendingVendors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('is_approved', false)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Vendor[];
    },
  });
};

export const useApprovedVendors = () => {
  return useQuery({
    queryKey: ['approvedVendors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Vendor[];
    },
  });
};

export const useApproveVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (vendorId: string) => {
      const { data, error } = await supabase
        .rpc('admin_approve_vendor', { 
          p_vendor_id: vendorId, 
          p_approved: true 
        });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingVendors'] });
      queryClient.invalidateQueries({ queryKey: ['approvedVendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
};

export const useRejectVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (vendorId: string) => {
      // Primero rechazamos (marcamos como no aprobado permanentemente)
      // Opcionalmente podríamos eliminar el vendor
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', vendorId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingVendors'] });
      queryClient.invalidateQueries({ queryKey: ['approvedVendors'] });
    },
  });
};

// 010 · Métrica de recuperación de contraseña (§10). Lee el RPC admin-only que cuenta
// los audit logs de GoTrue (sin tabla propia). Se muestra en el panel /admin.
export interface AuthRecoveryStats {
  window_days: number;
  requested: number;
  completed: number;
  completion_rate: number;
}

export const useAuthRecoveryStats = (days = 30) => {
  return useQuery({
    queryKey: ['authRecoveryStats', days],
    queryFn: async (): Promise<AuthRecoveryStats> => {
      const { data, error } = await supabase.rpc('get_auth_recovery_stats', { p_days: days });
      if (error) throw error;
      return data as unknown as AuthRecoveryStats;
    },
  });
};
